import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fab, Rating, TextField, Tooltip, Typography } from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import type { User } from 'firebase/auth';
import { config } from '../config/env';

type FeedbackFabProps = {
  visible: boolean;
  user: User | null;
  language: string;
};

const getLabels = (language: string) => {
  switch (language) {
    case 'en':
      return {
        tooltip: 'Send feedback',
        title: 'Feedback',
        placeholder: 'What can we improve?',
        cancel: 'Cancel',
        send: 'Send',
        sent: 'Thanks for your feedback!',
        error: 'Feedback could not be sent. Please try again.',
        authError: 'Please sign in again and try once more.',
        accessError: 'You do not have permission to send feedback.',
        validationError: 'Please check your entries and try again.',
        minLengthHint: 'Please enter at least 10 characters.',
        ratingLabel: 'Rating (0-5 stars)',
        ratingHint: 'Click again to clear stars to 0.',
      };
    case 'fr':
      return {
        tooltip: 'Envoyer un retour',
        title: 'Retour',
        placeholder: 'Que pouvons-nous améliorer ?',
        cancel: 'Annuler',
        send: 'Envoyer',
        sent: 'Merci pour votre retour !',
        error: "Le retour n'a pas pu être envoyé. Veuillez réessayer.",
        authError: 'Veuillez vous reconnecter puis réessayer.',
        accessError: "Vous n'avez pas l'autorisation d'envoyer un feedback.",
        validationError: 'Veuillez verifier vos donnees puis reessayer.',
        minLengthHint: 'Veuillez saisir au moins 10 caractères.',
        ratingLabel: 'Evaluation (0-5 etoiles)',
        ratingHint: 'Cliquez a nouveau pour remettre la note a 0.',
      };
    case 'it':
      return {
        tooltip: 'Invia feedback',
        title: 'Feedback',
        placeholder: 'Cosa possiamo migliorare?',
        cancel: 'Annulla',
        send: 'Invia',
        sent: 'Grazie per il tuo feedback!',
        error: "Impossibile inviare il feedback. Riprova.",
        authError: 'Effettua di nuovo l accesso e riprova.',
        accessError: 'Non hai il permesso di inviare feedback.',
        validationError: 'Controlla i dati inseriti e riprova.',
        minLengthHint: 'Inserisci almeno 10 caratteri.',
        ratingLabel: 'Valutazione (0-5 stelle)',
        ratingHint: 'Clicca di nuovo per azzerare a 0.',
      };
    default:
      return {
        tooltip: 'Feedback senden',
        title: 'Feedback',
        placeholder: 'Was koennen wir verbessern?',
        cancel: 'Abbrechen',
        send: 'Senden',
        sent: 'Danke für dein Feedback!',
        error: 'Feedback konnte nicht gesendet werden. Bitte erneut versuchen.',
        authError: 'Bitte melde dich erneut an und versuche es noch einmal.',
        accessError: 'Du hast keine Berechtigung, Feedback zu senden.',
        validationError: 'Bitte pruefe deine Eingaben und versuche es erneut.',
        minLengthHint: 'Bitte mindestens 10 Zeichen eingeben.',
        ratingLabel: 'Bewertung (0-5 Sterne)',
        ratingHint: 'Erneut klicken, um auf 0 Sterne zu setzen.',
      };
  }
};

export default function FeedbackFab({ visible, user, language }: FeedbackFabProps) {
  const labels = useMemo(() => getLabels(language), [language]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number | null>(0);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = window.setTimeout(() => {
      setSuccessMessage('');
      setErrorMessage('');
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [successMessage, errorMessage]);

  if (!visible) return null;

  const closeDialog = () => {
    if (submitting) return;
    setOpen(false);
    setErrorMessage('');
  };

  const submitFeedback = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 10 || !user) return;

    try {
      setSubmitting(true);
      setErrorMessage('');
      setSuccessMessage('');

      const token = await user.getIdToken();
      const response = await fetch(`${config.backend.fullApiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: trimmed,
          rating: rating ?? 0,
          language,
          source: 'feedback-fab',
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) throw new Error(labels.authError);
        if (response.status === 403) throw new Error(labels.accessError);
        if (response.status === 400) throw new Error(labels.validationError);
        const fallbackError = typeof payload?.error === 'string' && payload.error.trim()
          ? payload.error
          : labels.error;
        throw new Error(fallbackError);
      }

      setSuccessMessage(labels.sent);
      setMessage('');
      setRating(0);
      setOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || labels.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title={labels.tooltip} placement="right">
        <Fab
          onClick={() => {
            setOpen(true);
            setErrorMessage('');
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 1400,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px rgba(16,185,129,0.4)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #0ea271 0%, #047857 100%)',
              transform: 'scale(1.08)',
              boxShadow: '0 6px 20px rgba(16,185,129,0.5)',
            },
          }}
        >
          <FeedbackOutlinedIcon sx={{ fontSize: 26 }} />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{labels.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 0.5, mb: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {labels.ratingLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating
                name="feedback-rating"
                value={rating}
                max={5}
                onChange={(_event, newValue) => setRating(newValue ?? 0)}
                disabled={submitting}
              />
              <Typography variant="body2" color="text.secondary">
                {rating ?? 0}/5
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {labels.ratingHint}
            </Typography>
          </Box>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={labels.placeholder}
            disabled={submitting}
            sx={{ mt: 0.5 }}
          />
          {message.trim().length > 0 && message.trim().length < 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {labels.minLengthHint}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>{labels.cancel}</Button>
          <Button onClick={submitFeedback} variant="contained" disabled={submitting || message.trim().length < 10}>
            {submitting ? <CircularProgress size={18} color="inherit" /> : labels.send}
          </Button>
        </DialogActions>
      </Dialog>

      {(successMessage || errorMessage) && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 92,
            left: 24,
            zIndex: 1400,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            bgcolor: successMessage ? 'rgba(16,185,129,0.95)' : 'rgba(211,47,47,0.95)',
            color: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            maxWidth: 320,
          }}
        >
          <Typography variant="body2">{successMessage || errorMessage}</Typography>
        </Box>
      )}
    </>
  );
}
